import { useMemo, useState, type ReactElement } from 'react';
import { usePageBreadcrumb, usePageSidebar, useStrapiApi, useStrapiParams } from '@/hooks';
import { sidebarItems } from './sidebar';
import { MadridMap, NotariosList, SectionTitle } from '@/components';
import type { Notario, StrapiListRequest, StrapiListResponse } from '@/interfaces/strapi';
import { getNotarios } from '@/services/strapi';

type SelectedPayload = {
	codigoMunicipio?: string;
	title?: string;
};

export const BuscadorNotarios = (): ReactElement => {
	const [selectedMunicipioName, setSelectedMunicipioName] = useState<string | undefined>(undefined);
	const [page, setPage] = useState(1);
	const pageSize = 5;

	const filters = useMemo(() => {
		if (!selectedMunicipioName) return {} as any;
		return {
			notariaMunicipio: { $eq: selectedMunicipioName },
		};
	}, [selectedMunicipioName]);

	const handleMunicipioChange = (p: SelectedPayload) => {
		setSelectedMunicipioName(p.title);
		setPage(1);
	};

	const notarioParams = useStrapiParams({
		page,
		pageSize,
		sort: 'name:asc',
		additionalFilters: filters,
	});

	const {
		data: notariosResp,
		loading: loadingNotarios,
		error: errorNotarios,
	} = useStrapiApi<StrapiListResponse<Notario>, StrapiListRequest>(getNotarios, {
		autoFetch: true,
		params: notarioParams,
	});
	const notarios = notariosResp?.list ?? [];
	const pagination = notariosResp?.pagination;

	usePageBreadcrumb([{ label: 'El Colegio' }, { label: 'Buscador de notarios' }]);

	const sidebar = useMemo(() => sidebarItems, []);
	usePageSidebar(sidebar, true);

	return (
		<div>
			<SectionTitle title="Buscador de notarios" tag="h1" align="left" className="u-mb-16" />
			<div className="o-strapi-page">
				<MadridMap onSelectChange={handleMunicipioChange} />
			</div>
			<div className="u-mt-24">
				<h2 className="u-mb-8">
					{selectedMunicipioName
						? `Notarios en ${selectedMunicipioName}`
						: 'Selecciona un municipio en el mapa para ver los notarios de esa región'}
				</h2>

				{loadingNotarios && <p>Cargando notarios…</p>}
				{errorNotarios && <p>Error cargando notarios.</p>}

				{!loadingNotarios && !errorNotarios && (
					<NotariosList
						items={notarios}
						page={pagination?.page ?? page}
						pageSize={pagination?.pageSize ?? pageSize}
						total={pagination?.total ?? 0}
						isLoading={loadingNotarios}
						onPageChange={(newPage) => {
							if (newPage !== page) {
								setPage(newPage);
								window.scrollTo({ top: 0, behavior: 'smooth' });
							}
						}}
					/>
				)}
			</div>
		</div>
	);
};
